import { formatRoadmapMonth, roadmapEntries } from "@/lib/content/roadmap";
import {
  buildComparisonPath,
  comparisonIdentities,
} from "@/lib/comparison-links";
import { publicSitePublicationFlags } from "@/lib/publication-flags";

export const homeSectionAnchors = {
  product: "home-product",
  useCases: "home-use-cases",
  features: "home-features",
  aiAgents: "home-ai-agents",
  testimonials: "home-testimonials",
  guidedRollout: "home-guided-rollout",
  pricing: "pricing",
} as const;

export const productSectionAnchors = {
  overview: homeSectionAnchors.product,
  "coop-profile": "home-product-coop-profile",
  fundamentals: "home-product-fundamentals",
  pulse: "home-product-pulse",
  candidates: "home-product-candidates",
  "random-coffee": "home-product-random-coffee",
} as const;

export function buildHomeProductTabHref(
  pillar: keyof typeof productSectionAnchors,
): `/${string}` {
  return `/?pillar=${pillar}#${homeSectionAnchors.product}`;
}

export const homeSectionOrder = [
  homeSectionAnchors.product,
  homeSectionAnchors.useCases,
  homeSectionAnchors.features,
  homeSectionAnchors.aiAgents,
  homeSectionAnchors.testimonials,
  homeSectionAnchors.guidedRollout,
  homeSectionAnchors.pricing,
] as const;

export type SiteNavIcon =
  | "overview"
  | "coop-profile"
  | "fundamentals"
  | "pulse"
  | "candidates"
  | "random-coffee"
  | "partner";

export type SiteNavMenuTagIcon =
  | "slack"
  | "telegram"
  | "claude"
  | "linear"
  | "notion";

export type SiteNavMenuTag = {
  label: string;
  icon: SiteNavMenuTagIcon;
};

export type SiteNavLink = {
  title: string;
  href: string;
  description?: string;
  showInMobileMenu?: boolean;
  icon?: SiteNavIcon;
  menuVariant?: "feature";
  menuEyebrow?: string;
  menuTitle?: string;
  menuTitleHref?: string;
  menuDescription?: string;
  menuMeta?: string;
  menuCount?: number;
  menuTags?: readonly SiteNavMenuTag[];
};

export type SiteNavMenuSection = {
  label?: string;
  links: readonly SiteNavLink[];
};

export type SiteNavItem =
  | {
      name: string;
      kind: "menu";
      matchPaths: readonly string[];
      menuWidth?: string;
      menuGridTemplateColumns?: string;
      compact?: boolean;
      menuSections: readonly SiteNavMenuSection[];
    }
  | {
      name: string;
      kind: "link";
      href: string;
      matchPaths: readonly string[];
      icon?: "manifesto" | "insights";
    };

const nextPlannedRoadmapEntry = [...roadmapEntries]
  .filter((entry) => entry.phase === "planned")
  .sort((left, right) => {
    if (left.month !== right.month) {
      return left.month.localeCompare(right.month);
    }

    return (left.order ?? 0) - (right.order ?? 0);
  })[0];

export const primaryNavItems = [
  {
    name: "Product",
    kind: "menu",
    matchPaths: ["/roadmap", "/integrations"],
    menuWidth: "w-fit max-w-[calc(100vw-3rem)]",
    menuGridTemplateColumns: "minmax(0,1.05fr) minmax(18.5rem,0.95fr)",
    compact: true,
    menuSections: [
      {
        links: [
          {
            title: "Overview",
            href: buildHomeProductTabHref("overview"),
            icon: "overview",
          },
          {
            title: "Coop Profile",
            href: buildHomeProductTabHref("coop-profile"),
            icon: "coop-profile",
          },
          {
            title: "Fundamentals",
            href: buildHomeProductTabHref("fundamentals"),
            icon: "fundamentals",
          },
          {
            title: "Pulse",
            href: buildHomeProductTabHref("pulse"),
            icon: "pulse",
          },
          {
            title: "Candidates",
            href: buildHomeProductTabHref("candidates"),
            icon: "candidates",
          },
          {
            title: "Random Coffee",
            href: buildHomeProductTabHref("random-coffee"),
            icon: "random-coffee",
          },
        ],
      },
      {
        links: [
          {
            title: "Roadmap",
            href: "/roadmap",
            menuVariant: "feature",
            menuTitle: nextPlannedRoadmapEntry?.title ?? "Roadmap",
            menuTitleHref: nextPlannedRoadmapEntry?.anchorId
              ? `/roadmap#${nextPlannedRoadmapEntry.anchorId}`
              : "/roadmap",
            menuMeta: nextPlannedRoadmapEntry
              ? formatRoadmapMonth(nextPlannedRoadmapEntry.month)
              : undefined,
            menuCount: 5,
          },
          {
            title: "Integrations",
            href: "/integrations",
            menuVariant: "feature",
            menuCount: 3,
            menuTags: [
              { label: "Slack", icon: "slack" },
              { label: "Claude", icon: "claude" },
              { label: "Linear", icon: "linear" },
            ],
          },
        ],
      },
    ],
  },
  ...(publicSitePublicationFlags.learnHub
    ? [
        {
          name: "Learn",
          kind: "menu",
          matchPaths: ["/resources", "/guides", "/stories"],
          menuWidth: "w-[min(22rem,calc(100vw-2rem))]",
          compact: true,
          menuSections: [
            {
              links: [
                ...(publicSitePublicationFlags.guides
                  ? [
                      {
                        title: "Cooperly 101",
                        href: "/guides",
                        description: "Practical setup guides.",
                      },
                    ]
                  : []),
                ...(publicSitePublicationFlags.stories
                  ? [
                      {
                        title: "Stories",
                        href: "/stories",
                        description: "Short customer stories.",
                      },
                    ]
                  : []),
              ],
            },
          ],
        },
      ] as const
    : []),
  {
    name: "AI Context Layer",
    kind: "link",
    href: "/ai-context-layer",
    matchPaths: ["/ai-context-layer", "/ai-agents"],
  },
  {
    name: "Pricing",
    kind: "link",
    href: `/#${homeSectionAnchors.guidedRollout}`,
    matchPaths: [],
  },
  {
    name: "Manifesto",
    kind: "link",
    href: "/manifesto",
    matchPaths: ["/manifesto"],
    icon: "manifesto",
  },
  {
    name: "Insights",
    kind: "link",
    href: "/insights",
    matchPaths: ["/insights"],
    icon: "insights",
  },
] as const satisfies readonly SiteNavItem[];

export const footerNavGroups = [
  {
    label: "Product",
    links: [
      { title: "Overview", href: buildHomeProductTabHref("overview") },
      { title: "Coop Profile", href: buildHomeProductTabHref("coop-profile") },
      { title: "Fundamentals", href: buildHomeProductTabHref("fundamentals") },
      { title: "Pulse", href: buildHomeProductTabHref("pulse") },
      { title: "Candidates", href: buildHomeProductTabHref("candidates") },
      { title: "Random Coffee", href: buildHomeProductTabHref("random-coffee") },
      { title: "Roadmap", href: "/roadmap" },
      { title: "Integrations", href: "/integrations" },
      { title: "AI Context Layer", href: "/ai-context-layer" },
    ],
  },
  {
    label: "Compare",
    href: "/compare",
    links: [
      ...comparisonIdentities.map((entry) => ({
        title: `vs ${entry.competitor}`,
        href: buildComparisonPath(entry.slug),
      })),
    ],
  },
  {
    label: "Company",
    links: [
      { title: "Manifesto", href: "/manifesto" },
      { title: "Partner Program", href: "/partner-program", icon: "partner" },
      { title: "Privacy Policy", href: "/privacy-policy" },
      { title: "Terms & Conditions", href: "/terms-conditions" },
    ],
  },
  {
    label: "Learn",
    links: [
      { title: "Manual of Me", href: "/create-a-manual-for-yourself" },
      { title: "Insights", href: "/insights" },
      ...(publicSitePublicationFlags.guides
        ? [{ title: "Cooperly 101", href: "/guides" }]
        : []),
      ...(publicSitePublicationFlags.stories
        ? [{ title: "Stories", href: "/stories" }]
        : []),
    ],
  },
] as const satisfies readonly {
  label: string;
  href?: string;
  links: readonly SiteNavLink[];
}[];
